import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Chip,
    Icon,
    IconButton,
    InputAdornment,
    ListSubheader,
    Menu,
    MenuItem,
    Popover,
    TextField,
    Typography,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import clsx from 'clsx';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from 'app/cluster-styles/useStyles';
import * as Actions from '../store/actions';
import { IMAGE_TYPE, IMAGE_MODE } from '../../util/enums';
import SectionCard from './SectionCard';
import ClusterTagsInput from './ClusterTagsInput';

function getHostnameError(value, isEditMode) {
    if (!isEditMode || !value) return '';
    if (value.length > 40) return 'Name cannot exceed 40 characters.';
    if (/[^a-z0-9-]/.test(value)) return 'Use only lowercase letters, numbers, and hyphens.';
    if (/^-|-$|--/.test(value)) return 'Hyphens cannot start or end the name.';
    if (/^\d/.test(value)) return 'Name cannot start with a number.';
    return '';
}

export default function BasicInformationSection({
    form, setField, provider, allowedOsTypes, disableStackFields,
    availableArchs, selectedArch, onArchChange, onOsChange,
    hideImage, hideOs,
}) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const supportedOSs = useSelector(({ clusters }) => clusters.cluster.os_types) || [];
    const stacks = useSelector(({ stacks }) => stacks.stack.list) || [];

    const isEditMode = Boolean(form.id);

    const [hostnameInfoAnchor, setHostnameInfoAnchor] = useState(null);
    const textFieldRef = useRef(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [prefixError, setPrefixError] = useState('');
    // image-customization: captured versions of the selected stack. Selecting a specific
    // version sets serverImageName to that version's full "<prefix>-server-<ts>" name,
    // which is itself a unique prefix - data.tf resolves it to exactly that image
    // (rollback). "Default (newest)" reverts serverImageName to the bare prefix.
    const [versions, setVersions] = useState([]);
    const [versionStackId, setVersionStackId] = useState(null);

    useEffect(() => {
        if (provider) dispatch(Actions.provider_os_types(provider));
    }, [dispatch, provider]);

    const filteredOSs = allowedOsTypes
        ? supportedOSs.filter((os) => allowedOsTypes.includes(os))
        : supportedOSs;

    const providerStacks = stacks.filter(
        (stack) => stack.info?.state === 'success' && stack?.provider === provider
    );

    const validatePrefix = (prefix) => {
        if (!prefix) { setPrefixError(''); return; }
        if (/[^a-z0-9-]/g.test(prefix)) {
            setPrefixError('Invalid characters. Use only lowercase letters, numbers, and hyphens.');
        } else if (/^-|-$|--/g.test(prefix)) {
            setPrefixError('Hyphens cannot start or end, and consecutive hyphens are not allowed.');
        } else {
            setPrefixError('');
        }
    };

    const displayName = form.displayName || '';
    const hostname = form.hostname || '';
    const osType = form.osType || '';
    const tags = form.tags || [];
    const serverImageName = form.serverImageName || '';
    const serverImageId = form.serverImageId || '';
    const serverImageType = form.serverImageType || IMAGE_TYPE.HEIDI_STACK;
    const imageMode = form.imageMode || IMAGE_MODE.UNIFIED;

    const hostnameError = getHostnameError(hostname, isEditMode);

    // Feature: image-customization - IBM uses a selection-only image picker (no typing):
    // a "Stacks" group (Heidi-built, by prefix/version) + a "Cloud images" group
    // (private images discovered in the account, pinned by explicit id). Phase 1: IBM.
    const isIbm = provider === 'ibm';
    const imageArch = /arm64|aarch64|(^|[^a-z])arm([^a-z]|$)/.test(osType.toLowerCase()) ? 'arm64' : 'amd64';
    const [cloudImages, setCloudImages] = useState([]);

    useEffect(() => {
        // When hideImage is set (IBM renders the image picker in its own section), skip
        // the fetch here so it isn't done twice.
        if (hideImage || !isIbm || !form.regionId || !form.credentialId) { setCloudImages([]); return; }
        const params = new URLSearchParams({
            provider, region: form.regionId, credentialId: form.credentialId, architecture: imageArch
        });
        // Reuse the infrastructure-verification service (CLI-based, same as zone checks).
        axios.get(`${API_BASE_URL}/api/instance-availability/images?${params.toString()}`)
            .then((r) => setCloudImages((r.data && r.data.images) || []))
            .catch(() => setCloudImages([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIbm, provider, form.regionId, form.credentialId, imageArch]);

    // Resolve the grouped-select value from the form: a pinned cloud image (by id) wins,
    // else the stack whose prefix matches the current image name.
    const matchedStack = providerStacks.find(
        (s) => s.data && s.data.prefix &&
            (serverImageName === s.data.prefix || serverImageName.indexOf(s.data.prefix + '-') === 0)
    );
    const imageSelectValue = serverImageId
        ? `cloud:${serverImageId}`
        : (matchedStack ? `stack:${matchedStack.data.prefix}` : '');

    const handleImageSelect = (val) => {
        if (val && val.indexOf('stack:') === 0) {
            const prefix = val.slice(6);
            setField('serverImageId', '');
            setField('serverImageName', prefix);
            validatePrefix(prefix);
        } else if (val && val.indexOf('cloud:') === 0) {
            const id = val.slice(6);
            const img = cloudImages.find((i) => i.id === id);
            setField('serverImageId', id);
            // image_name is required by the model; store the image's name for display.
            // data.tf uses the explicit id (by_id), so the name is not used for resolution.
            setField('serverImageName', img ? img.name : id);
        }
    };

    // Load captured image versions for the stack matching the current image name.
    useEffect(() => {
        if (hideImage) { return; } // IBM image picker (incl. versions) lives in its own section
        // Match the bare prefix ("heidi-u26") OR any captured version name for this
        // prefix ("heidi-u26-heidi-<ts>" unified, "heidi-u26-server-<ts>" multi-image).
        const match = providerStacks.find(
            (s) => s.data && s.data.prefix &&
                (serverImageName === s.data.prefix || serverImageName.indexOf(s.data.prefix + '-') === 0)
        );
        if (match && match.id) {
            if (match.id !== versionStackId) {
                setVersionStackId(match.id);
                axios.get(`${API_BASE_URL}/api/stacks/${match.id}/versions`)
                    .then((r) => setVersions(r.data || []))
                    .catch(() => setVersions([]));
            }
        } else if (versionStackId) {
            setVersionStackId(null);
            setVersions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverImageName, stacks]);

    const selectedStack = providerStacks.find((s) => s.id === versionStackId);
    const versionSelectValue = versions.some((v) => v.name === serverImageName) ? serverImageName : '__default__';

    return (
        <SectionCard
            icon="info"
            title="Basic Information"
            action={isEditMode && form.id && (
                <Typography style={{ color: '#666', fontSize: '14px', fontWeight: 'normal' }}>
                    ID: {form.id}
                </Typography>
            )}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {/* Left Column */}
                <div style={{ width: hideImage ? '100%' : '48%' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <TextField
                            className={clsx(classes.input)}
                            style={{ flex: 2 }}
                            id={`${provider}_displayName`}
                            name="displayName"
                            label="Cluster Name"
                            type="text"
                            variant="outlined"
                            value={displayName}
                            onChange={(e) => setField('displayName', e.target.value)}
                            required
                            placeholder="Cluster Name"
                            error={Boolean(displayName && displayName.length > 128)}
                            helperText={displayName && displayName.length > 128 ? 'Display name cannot exceed 128 characters.' : ''}
                        />
                        {Array.isArray(availableArchs) && availableArchs.length > 0 && (
                            <TextField
                                select
                                className={clsx(classes.input)}
                                style={{ flex: 1, minWidth: 140 }}
                                id={`${provider}_arch`}
                                name="arch"
                                label="Architecture"
                                variant="outlined"
                                value={selectedArch || ''}
                                onChange={(e) => onArchChange && onArchChange(e.target.value)}
                                required
                                disabled={availableArchs.length <= 1}
                            >
                                {availableArchs.map((a) => (
                                    <MenuItem key={a} id={`arch-${a}`} value={a}>{a}</MenuItem>
                                ))}
                            </TextField>
                        )}
                        {/* OS is hidden when hideOs is set (IBM derives it from the selected image). */}
                        {!hideOs && (
                        <TextField
                            select
                            className={clsx(classes.input)}
                            style={{ flex: 1, minWidth: 160 }}
                            id={`${provider}_os-type`}
                            name="os-type"
                            label="Operating System"
                            variant="outlined"
                            value={osType}
                            onChange={(e) => {
                                // In variant-aware marketplace mode, route through
                                // onOsChange so it updates `form.marketOs` (which
                                // drives variant resolution). The variant-change
                                // effect in useMarketplace will then sync osType
                                // to the variant's os_types. Without this, the OS
                                // picker would only set osType, the variant
                                // wouldn't change, and the effect would reset
                                // osType right back to the old value.
                                if (onOsChange) onOsChange(e.target.value);
                                else setField('osType', e.target.value);
                            }}
                            required
                            disabled={filteredOSs.length <= 1}
                            SelectProps={{
                                MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                            }}
                        >
                            {filteredOSs.map((os, i) => (
                                <MenuItem key={i} id={`os-${os}`} value={os}>{os}</MenuItem>
                            ))}
                        </TextField>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '16px', alignItems: 'flex-start' }}>
                        <TextField
                            className={clsx(classes.input)}
                            style={{ flex: 1 }}
                            id={`${provider}_name`}
                            name="name"
                            label="Hostname"
                            type="text"
                            variant="outlined"
                            value={hostname}
                            onChange={(e) => setField('hostname', e.target.value)}
                            disabled={!isEditMode}
                            required={isEditMode}
                            placeholder={!isEditMode ? 'Auto-generated on save' : 'Technical hostname (no spaces or capitals)'}
                            error={Boolean(hostnameError)}
                            helperText={hostnameError}
                            InputProps={!isEditMode ? {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Icon
                                            style={{ color: '#2196F3', cursor: 'pointer' }}
                                            onClick={(e) => setHostnameInfoAnchor(e.currentTarget)}
                                        >info_outline</Icon>
                                        <Popover
                                            open={Boolean(hostnameInfoAnchor)}
                                            anchorEl={hostnameInfoAnchor}
                                            onClose={() => setHostnameInfoAnchor(null)}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            <Box p={2} style={{ maxWidth: '300px' }}>
                                                <Typography variant="body2">
                                                    The hostname is auto-generated from your cluster name when saved. You can customize it by editing the cluster afterwards.
                                                </Typography>
                                            </Box>
                                        </Popover>
                                    </InputAdornment>
                                )
                            } : undefined}
                        />
                        <ClusterTagsInput
                            tags={tags}
                            onChange={(newTags) => setField('tags', newTags)}
                            style={{ flex: 1 }}
                        />
                    </div>
                </div>

                {/* Divider (hidden when the image picker is rendered in its own section) */}
                {!hideImage && <div style={{ width: '2px', backgroundColor: '#e0e0e0', margin: '10px 0', alignSelf: 'stretch' }} />}

                {/* Right Column */}
                {!hideImage && (
                <div style={{ width: '48%' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        {isIbm ? (
                            /* Selection-only image picker: Stacks + Cloud images. No typing. */
                            <TextField
                                select
                                label="Image"
                                className={clsx(classes.input)}
                                style={{ flex: 1 }}
                                required
                                disabled={disableStackFields}
                                value={imageSelectValue}
                                variant="outlined"
                                helperText="Pick a Heidi stack or an existing image from your cloud account."
                                SelectProps={{ displayEmpty: true, renderValue: (v) => {
                                    if (!v) return 'Select an image';
                                    if (v.indexOf('stack:') === 0) return v.slice(6);
                                    const img = cloudImages.find((i) => `cloud:${i.id}` === v);
                                    return img ? img.name : v.slice(6);
                                } }}
                                onChange={(e) => handleImageSelect(e.target.value)}
                            >
                                <ListSubheader>Stacks</ListSubheader>
                                {providerStacks.length > 0
                                    ? providerStacks.map((stack) => (
                                        <MenuItem key={`stack:${stack.id}`} value={`stack:${stack.data.prefix}`}>
                                            {stack.displayName || stack.name}
                                            <span style={{ color: '#94a3b8', marginLeft: 6 }}>({stack.data.prefix})</span>
                                        </MenuItem>
                                    ))
                                    : <MenuItem disabled>No stacks built for this provider</MenuItem>}
                                <ListSubheader>Cloud images</ListSubheader>
                                {cloudImages.length > 0
                                    ? cloudImages.map((img) => (
                                        <MenuItem key={`cloud:${img.id}`} value={`cloud:${img.id}`}>
                                            <span style={{ flex: 1 }}>{img.name}</span>
                                            <Chip
                                                size="small"
                                                className="ml-8"
                                                style={{ background: '#e0f2fe', color: '#075985' }}
                                                label={img.os ? `${img.os}` : (img.arch || 'image')}
                                            />
                                        </MenuItem>
                                    ))
                                    : <MenuItem disabled>{form.regionId && form.credentialId ? 'No cloud images found' : 'Select region + credential first'}</MenuItem>}
                            </TextField>
                        ) : (
                            <>
                            <TextField
                                ref={textFieldRef}
                                label="Stack"
                                className={clsx(classes.input)}
                                style={{ flex: 1 }}
                                placeholder="Your Image Name or Select Stack"
                                required
                                disabled={disableStackFields}
                                value={serverImageName}
                                onChange={(e) => {
                                    setField('serverImageName', e.target.value);
                                    validatePrefix(e.target.value);
                                }}
                                variant="outlined"
                                error={!!prefixError}
                                helperText={prefixError}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                disabled={disableStackFields}
                                                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                                            >
                                                <ArrowDropDownIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Menu
                                anchorEl={menuAnchorEl}
                                open={Boolean(menuAnchorEl)}
                                onClose={() => setMenuAnchorEl(null)}
                            >
                                {providerStacks.length > 0
                                    ? providerStacks.map((stack, i) => (
                                        <MenuItem
                                            key={i}
                                            onClick={() => {
                                                setField('serverImageName', stack.data.prefix);
                                                validatePrefix(stack.data.prefix);
                                                setMenuAnchorEl(null);
                                            }}
                                        >
                                            {stack.data.prefix}
                                        </MenuItem>
                                    ))
                                    : <MenuItem disabled>No stack builds available for this provider</MenuItem>
                                }
                            </Menu>
                            </>
                        )}

                        {!serverImageId && versions.length > 0 && (
                            <TextField
                                select
                                label="Image Version"
                                className={clsx(classes.input)}
                                style={{ flex: 1 }}
                                disabled={disableStackFields}
                                value={versionSelectValue}
                                variant="outlined"
                                helperText="Pick an older version to roll back; default uses the newest."
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '__default__') {
                                        setField('serverImageName', selectedStack ? selectedStack.data.prefix : serverImageName);
                                    } else {
                                        setField('serverImageName', val);
                                    }
                                }}
                            >
                                <MenuItem value="__default__">Default (newest)</MenuItem>
                                {versions.map((v) => (
                                    <MenuItem key={v._id || v.imageId} value={v.name}>
                                        {v.name}{v.isDefault ? ' (default)' : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        <TextField
                            select
                            className={clsx(classes.input)}
                            id={`${provider}_server_image_type`}
                            name="server_image_type"
                            label="Image Type"
                            variant="outlined"
                            value={serverImageType}
                            onChange={(e) => setField('serverImageType', e.target.value)}
                            style={{ width: '180px' }}
                            disabled={disableStackFields}
                        >
                            <MenuItem value={IMAGE_TYPE.HEIDI_STACK}>Heidi Stack</MenuItem>
                            <MenuItem value={IMAGE_TYPE.CUSTOM_STACK}>Custom Stack</MenuItem>
                        </TextField>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginTop: '16px' }}>
                        <TextField
                            select
                            className={clsx(classes.input)}
                            id={`${provider}_image_mode`}
                            name="image_mode"
                            label="Image Mode"
                            variant="outlined"
                            value={imageMode}
                            onChange={(e) => setField('imageMode', e.target.value)}
                            style={{ width: '220px' }}
                            disabled={disableStackFields}
                        >
                            <MenuItem value={IMAGE_MODE.UNIFIED}>Unified (Single Image)</MenuItem>
                            <MenuItem value={IMAGE_MODE.MULTI_IMAGE}>Multi-Image (Legacy)</MenuItem>
                        </TextField>
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px', maxWidth: '300px' }}>
                            {imageMode === IMAGE_MODE.UNIFIED
                                ? 'Single unified image for head and compute nodes'
                                : 'Separate images for head node and compute nodes'}
                        </Typography>
                    </div>
                </div>
                )}
            </div>
        </SectionCard>
    );
}
